// Results → Channel Insights (/brand-os/channel-insights)
// Phase 11E · Facebook Page + Instagram account-level insights.
//
// Meta App Review requires us to demonstrate exactly where each
// insights permission is used. This screen renders the Page and IG
// account side-by-side with 7- and 28-day totals, a daily line per
// metric, recent posts, and a per-tile "locked: requires {permission}"
// note whenever Meta refuses a metric.
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, Pill } from "../lib/ui";

// --------------------------------------------------------------------
// helpers
// --------------------------------------------------------------------

function fmtNum(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString();
}

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// --------------------------------------------------------------------
// Tiny inline SVG sparkline (no chart library needed).
// --------------------------------------------------------------------

function Sparkline({ series, testId, color = "var(--bo-accent, #0f766e)" }) {
  const points = (series || []).map((p) => Number(p.value) || 0);
  if (points.length < 2) {
    return (
      <div
        data-testid={testId}
        style={{ height: 48, color: "var(--bo-muted)", fontSize: 12,
                 display: "flex", alignItems: "center" }}
      >
        Not enough data for a chart yet.
      </div>
    );
  }
  const w = 220;
  const h = 48;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = Math.max(max - min, 1);
  const step = w / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = i * step;
    const y = h - ((v - min) / span) * (h - 6) - 3;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const path = `M${coords.join(" L")}`;
  return (
    <svg
      data-testid={testId}
      width="100%" height={h} viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: "block" }}
      aria-label="daily trend"
    >
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
}

// --------------------------------------------------------------------
// Reusable tile
// --------------------------------------------------------------------

function MetricTile({ title, shortTotal, longTotal, series, status, locked,
                      testId, shortDays, longDays, bigNumber, subline }) {
  if (locked) {
    const perm = locked.required_permission || "additional permission";
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <div className="bo-metric-value" style={{ color: "var(--bo-muted)" }}>
          Locked
        </div>
        <Pill tone="warn" testId={`${testId}-locked-pill`}>
          Locked · requires {perm}
        </Pill>
        <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 8 }}>
          Meta code {locked.code}: {locked.message}
        </div>
      </div>
    );
  }
  // Dropped / errored / no-data tiles — surface the reason so Roger
  // can tell "Meta refused the metric" apart from "no activity yet".
  const kind = status?.kind;
  if (kind === "unavailable_sandbox") {
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <div className="bo-metric-value" style={{ color: "var(--bo-muted)" }}>
          —
        </div>
        <Pill tone="warn" testId={`${testId}-sandbox-pill`}>
          Not supported on Pinterest's trial sandbox
        </Pill>
        {status?.message && status.message !== "Not supported on Pinterest's trial sandbox" && (
          <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 6 }}>
            {status.message}
          </div>
        )}
      </div>
    );
  }
  if (kind === "deprecated" || kind === "error") {
    const tone = kind === "deprecated" ? "warn" : "danger";
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <div className="bo-metric-value" style={{ color: "var(--bo-muted)" }}>
          —
        </div>
        <Pill tone={tone} testId={`${testId}-status-pill`}>
          {status.message}
        </Pill>
        {status.detail && (
          <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 6 }}>
            {status.detail}
          </div>
        )}
      </div>
    );
  }
  if (kind === "no_data"
      || (longTotal == null && (!series || series.length === 0))) {
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <div className="bo-metric-value" style={{ color: "var(--bo-muted)" }}>
          —
        </div>
        <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 4 }}
             data-testid={`${testId}-no-data`}>
          {status?.message || "No activity in this period"}
        </div>
      </div>
    );
  }
  return (
    <div className="bo-card" data-testid={testId}>
      <div className="bo-metric-label">{title}</div>
      <div className="bo-metric-value" data-testid={`${testId}-long`}>
        {fmtNum(bigNumber != null ? bigNumber : longTotal)}
      </div>
      <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 6 }}>
        {subline != null ? (
          <span data-testid={`${testId}-subline`}>{subline}</span>
        ) : (
          <>
            Last {longDays} days · last {shortDays}d:{" "}
            <span data-testid={`${testId}-short`}>{fmtNum(shortTotal)}</span>
          </>
        )}
      </div>
      <Sparkline series={series} testId={`${testId}-spark`} />
    </div>
  );
}

// --------------------------------------------------------------------
// Channel column (Facebook OR Instagram)
// --------------------------------------------------------------------

const FB_TILES = [
  { key: "page_follows",                 label: "Followers" },
  { key: "page_total_media_view_unique", label: "Reach (unique views)" },
  { key: "page_views_total",             label: "Page views" },
  { key: "page_post_engagements",        label: "Post engagements" },
];

const IG_TILES = [
  { key: "follower_count",     label: "Followers (last 30d)" },
  { key: "reach",              label: "Reach" },
  { key: "views",              label: "Views" },
  { key: "accounts_engaged",   label: "Accounts engaged" },
  { key: "total_interactions", label: "Interactions" },
  { key: "likes",              label: "Likes" },
  { key: "comments",           label: "Comments" },
  { key: "saves",              label: "Saves" },
];

const PINTEREST_TILES = [
  { key: "IMPRESSION",     label: "Impressions" },
  { key: "SAVE",           label: "Saves" },
  { key: "OUTBOUND_CLICK", label: "Outbound clicks" },
  { key: "PIN_CLICK",      label: "Pin clicks" },
];

function ChannelColumn({ title, block, tiles, shortDays, longDays, testId,
                         onRetryIg, loading }) {
  // Safety-by-Default (2026-10 Meta review fix): while a load is in
  // flight (or before the first response arrives) we MUST NOT render
  // "Not connected" — a Meta reviewer scrolling past the Insights tab
  // would see that flash and reject the submission even though the
  // Page + IG are actually connected. The response explicitly carries
  // `connected: false` only when we have evidence of no connection.
  if (loading && !block) {
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <div
          data-testid={`${testId}-loading`}
          style={{
            padding: "28px 12px",
            color: "var(--bo-muted)",
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span
              aria-hidden="true"
              style={{
                width: 14, height: 14, borderRadius: "50%",
                border: "2px solid var(--bo-muted)",
                borderTopColor: "transparent",
                animation: "ci-spin 0.9s linear infinite",
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: 14 }}>
              Loading live numbers from Meta…
            </span>
          </div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Meta Graph can take 10–20 seconds to answer; we never say
            "Not connected" until it does.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <span className="ci-skel ci-skel-pill" />
            <span className="ci-skel ci-skel-pill" />
            <span className="ci-skel ci-skel-pill" />
          </div>
        </div>
      </div>
    );
  }
  // Error block from backend: connection row exists but hydration
  // failed. Must NEVER render as "Not connected".
  if (block && block.connected && block.error) {
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <div data-testid={`${testId}-error`}
             style={{ padding: "20px 12px",
                      color: "var(--bo-muted)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8 }}>
          <div style={{ fontSize: 14, color: "var(--bo-fg)" }}>
            Couldn't refresh this tile right now.
          </div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            {block.error.message || "Try again in a moment."}
          </div>
        </div>
      </div>
    );
  }
  if (!block || !block.connected) {
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <EmptyState
          title="Not connected"
          description={`Connect ${title} on the Channel Connections admin to see insights here.`}
          testId={`${testId}-empty`}
        />
      </div>
    );
  }
  if (block.error) {
    return (
      <div className="bo-card" data-testid={testId}>
        <div className="bo-metric-label">{title}</div>
        <ErrorBanner
          error={`${block.error.code}: ${block.error.message}`}
          testId={`${testId}-error`}
        />
        {block.stale_ig_user_id && onRetryIg && (
          <button
            type="button"
            className="bo-btn"
            onClick={onRetryIg}
            data-testid={`${testId}-retry-ig`}
            style={{ marginTop: 8 }}
          >
            Retry Instagram discovery
          </button>
        )}
      </div>
    );
  }

  // Pinterest returns a single analytics envelope (not window + locked),
  // where ``.window.error`` indicates a Pinterest API refusal.
  const win = block.window || {};
  const shortWin = block.short_window || {};
  const pinterestError = win.error || null;

  return (
    <div data-testid={testId}>
      <div className="bo-card" style={{ marginBottom: 12 }}>
        <div className="bo-metric-label">{title}</div>
        <div style={{ fontSize: 20, fontFamily: "var(--bo-serif)" }}
             data-testid={`${testId}-display-name`}>
          {block.page_name || block.ig_username || block.username
            || block.board_name || block.connection_id}
        </div>
        {block.app_access_tier && (
          <div style={{ marginTop: 6 }}>
            <Pill
              tone={block.app_access_tier === "standard" ? "success" : "warn"}
              testId={`${testId}-tier`}
            >
              {block.app_access_tier} access
            </Pill>
            {block.sandbox && (
              <span style={{ fontSize: 11, marginLeft: 8,
                             color: "var(--bo-muted)" }}>
                sandbox · {block.api_base}
              </span>
            )}
          </div>
        )}
        {block.stale_ig_user_id && (
          <div style={{ marginTop: 8 }}>
            <Pill tone="warn" testId={`${testId}-stale-id-pill`}>
              Stale ig_user_id · tap Retry
            </Pill>
            {onRetryIg && (
              <button
                type="button"
                className="bo-btn"
                onClick={onRetryIg}
                data-testid={`${testId}-retry-ig`}
                style={{ marginLeft: 8 }}
              >
                Retry Instagram discovery
              </button>
            )}
          </div>
        )}
      </div>

      {pinterestError && (
        pinterestError.code && String(pinterestError.code).startsWith("http_4")
        && block.sandbox ? (
          <div style={{ marginTop: 8, padding: 10, borderRadius: 6,
                        background: "var(--bo-panel-alt, #faf8f2)",
                        color: "var(--bo-muted)" }}
               data-testid={`${testId}-sandbox-note`}>
            Not supported on Pinterest's trial sandbox. Pinterest
            analytics require Standard access.
          </div>
        ) : (
          <ErrorBanner
            error={`${pinterestError.code}: ${pinterestError.message}`}
            testId={`${testId}-pinterest-error`}
          />
        )
      )}

      <div
        className="ci-tiles"
      >
        {tiles.map((tile) => {
          const long = (win.data || {})[tile.key];
          const short = (shortWin.data || {})[tile.key];
          const lockedLong = (win.locked || {})[tile.key];
          const lockedShort = (shortWin.locked || {})[tile.key];
          const locked = lockedLong || lockedShort;

          // "Followers" tile on Facebook: Meta's `/insights?metric=page_follows`
          // only exposes the daily *delta*. The TOTAL follower count
          // lives on the Page object itself at
          // /{page_id}?fields=followers_count — surfaced to the UI as
          // `block.followers_count`. We use that as the big number
          // and render the daily-delta sum as a short subline so Roger
          // can see both at a glance.
          let bigNumber = null;
          let subline = null;
          if (tile.key === "page_follows"
              && block.followers_count != null
              && !locked) {
            bigNumber = block.followers_count;
            const delta = long?.total ?? 0;
            const sign = delta > 0 ? "+" : delta < 0 ? "−" : "±";
            const absN = Math.abs(delta || 0);
            subline = `${sign}${fmtNum(absN)} in last ${longDays} days`;
          }

          return (
            <MetricTile
              key={tile.key}
              title={tile.label}
              shortTotal={short?.total ?? short?.last ?? null}
              longTotal={long?.total ?? long?.last ?? null}
              series={long?.series}
              status={long?.status}
              locked={locked}
              shortDays={shortDays}
              longDays={longDays}
              bigNumber={bigNumber}
              subline={subline}
              testId={`${testId}-metric-${tile.key}`}
            />
          );
        })}
      </div>
      {(win.dropped || []).length > 0 && (
        <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 8 }}
             data-testid={`${testId}-dropped`}>
          Dropped {win.dropped.length} deprecated metric name
          {win.dropped.length === 1 ? "" : "s"}:{" "}
          {win.dropped.map((d) => d.metric).join(", ")}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------
// Recent-posts panel
// --------------------------------------------------------------------

function PostsPanel({ posts, testId }) {
  if (!posts || posts.length === 0) {
    return (
      <EmptyState
        title="No recent posts"
        description="Publish a Page post to see per-post reach and engagement here."
        testId={`${testId}-empty`}
      />
    );
  }
  return (
    <div className="ci-table-wrap" data-testid={testId}>
      <table className="bo-table" style={{ width: "100%", minWidth: 520 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Posted</th>
            <th style={{ textAlign: "left" }}>Message</th>
            <th style={{ textAlign: "right" }}>Reach</th>
            <th style={{ textAlign: "right" }}>Clicks</th>
            <th style={{ textAlign: "right" }}>Reactions</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => {
            const reach = p.metrics?.post_total_media_view_unique?.total;
            const clicks = p.metrics?.post_clicks?.total;
            const reactions = p.metrics?.post_reactions_by_type_total?.total;
            return (
              <tr key={p.id} data-testid={`${testId}-row-${p.id}`}>
                <td>{p.created_time ? fmtWhen(p.created_time) : "—"}</td>
                <td style={{ maxWidth: 420 }}>
                  <div style={{ overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap" }}>
                    {p.message || <span style={{ color: "var(--bo-muted)" }}>(no caption)</span>}
                  </div>
                </td>
                <td style={{ textAlign: "right" }}>{fmtNum(reach)}</td>
                <td style={{ textAlign: "right" }}>{fmtNum(clicks)}</td>
                <td style={{ textAlign: "right" }}>{fmtNum(reactions)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// --------------------------------------------------------------------
// Instagram media panel — per-post reach / views / likes / comments /
// saves. The backend sources this via `/{ig-user}/media` + per-media
// `/{ig-media}/insights` and already drops any metric the specific
// media_type refuses, so the UI never has to special-case REELS vs
// FEED vs CAROUSEL_ALBUM.
// --------------------------------------------------------------------

function IgMediaPanel({ media, testId }) {
  if (!media || media.length === 0) {
    return (
      <EmptyState
        title="No recent Instagram posts"
        description="Publish or cross-post something to Instagram to see per-post numbers here."
        testId={`${testId}-empty`}
      />
    );
  }

  function cell(metric) {
    if (!metric) return "—";
    const kind = metric.status?.kind;
    if (kind === "deprecated" || kind === "error") {
      return (
        <span style={{ color: "var(--bo-muted)", fontSize: 12 }}
              title={metric.status?.message}>
          n/a
        </span>
      );
    }
    const v = metric.total ?? metric.last;
    if (v == null) {
      return <span style={{ color: "var(--bo-muted)" }}>0</span>;
    }
    return fmtNum(v);
  }

  return (
    <div className="ci-table-wrap" data-testid={testId}>
      <table className="bo-table" style={{ width: "100%", minWidth: 680 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Posted</th>
            <th style={{ textAlign: "left" }}>Type</th>
            <th style={{ textAlign: "left" }}>Caption</th>
            <th style={{ textAlign: "right" }}>Reach</th>
            <th style={{ textAlign: "right" }}>Views</th>
            <th style={{ textAlign: "right" }}>Likes</th>
            <th style={{ textAlign: "right" }}>Comments</th>
            <th style={{ textAlign: "right" }}>Saves</th>
          </tr>
        </thead>
        <tbody>
          {media.map((m) => (
            <tr key={m.id} data-testid={`${testId}-row-${m.id}`}>
              <td>{m.timestamp ? fmtWhen(m.timestamp) : "—"}</td>
              <td style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                {m.media_product_type || m.media_type || "—"}
              </td>
              <td style={{ maxWidth: 320 }}>
                <div style={{ overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap" }}>
                  {m.permalink ? (
                    <a href={m.permalink} target="_blank" rel="noreferrer"
                       data-testid={`${testId}-link-${m.id}`}>
                      {m.caption || "(no caption)"}
                    </a>
                  ) : (m.caption || "(no caption)")}
                </div>
              </td>
              <td style={{ textAlign: "right" }}>{cell(m.metrics?.reach)}</td>
              <td style={{ textAlign: "right" }}>{cell(m.metrics?.views)}</td>
              <td style={{ textAlign: "right" }}>{cell(m.metrics?.likes)}</td>
              <td style={{ textAlign: "right" }}>{cell(m.metrics?.comments)}</td>
              <td style={{ textAlign: "right" }}>{cell(m.metrics?.saves)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --------------------------------------------------------------------
// Page root
// --------------------------------------------------------------------

export default function ChannelInsights() {
  // Live state: `report` is the latest successful fetch, `stale` is the
  // previous successful fetch kept during a failed refresh so the UI
  // never collapses to "Not connected" on a timeout. `error` carries a
  // plain-English message.
  const [report, setReport] = useState(null);
  const [stale, setStale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState(null);

  const load = useCallback(async (opts) => {
    const force = !!(opts && opts.force);
    setLoading(true);
    setError(null);
    try {
      const url = `/channel-insights/overview?days=28&short_days=7${
        force ? "&force=true" : ""
      }`;
      // Give Meta + Pinterest enough time on a cold cache. Backend
      // fetches each channel in parallel so this is a conservative cap,
      // not the sum of channel times.
      const r = await api.get(url, { timeout: 60000 });
      setReport(r.data);
      setStale(null);     // success → drop the cached fallback
    } catch (e) {
      // Safety-by-Default: a failed refresh MUST NOT blank the tiles.
      // Keep the last-good `report` in `stale` and surface a friendly
      // retry banner. Only render "Not connected" when a response says
      // `connected: false`.
      if (report) setStale(report);
      setReport(null);
      // Replace raw "timeout of 15000ms exceeded" with human copy.
      let msg = formatApiError(e);
      if (/timeout|ECONNABORTED|ETIMEDOUT/i.test(String(msg))) {
        msg = "Meta took longer than usual to respond. Showing the "
            + "last cached numbers below — tap Refresh to try again.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [report]);

  useEffect(() => {
    load();
  }, [load]);

  const onRetryIg = useCallback(async () => {
    setRetrying(true);
    setRetryMsg(null);
    try {
      const r = await api.post("/channel-insights/retry-ig-discovery", {});
      setRetryMsg(
        `Repair complete: scanned ${r.data?.scanned ?? 0}, repaired ${r.data?.repaired ?? 0}.`,
      );
      await load();
    } catch (e) {
      setRetryMsg(`Repair failed: ${formatApiError(e)}`);
    } finally {
      setRetrying(false);
    }
  }, [load]);

  const longDays = report?.window_days ?? 28;
  const shortDays = report?.short_window_days ?? 7;

  // Tile data = fresh report; on failed refresh, fall back to stale so
  // the UI never collapses to "Not connected".
  const activeReport = report || stale;
  const refreshFailedUsingStale = Boolean(stale && !report && error);

  return (
    <Shell>
      <style>{`
        /* Mobile: force channel-insights to one column under 768px so
           the Facebook / Instagram / Pinterest columns stack and the
           page never scrolls horizontally. Also wraps the data tables
           in a horizontally scrollable container so wide rows scroll
           inside the card instead of pushing the whole page sideways. */
        .ci-root { max-width: 100%; overflow-x: hidden; }
        .ci-root .ci-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 16px;
        }
        .ci-root .ci-tiles {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
        }
        .ci-root .ci-table-wrap {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .ci-root .ci-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 16px;
        }
        /* Loading indicator keyframe + pill-shaped skeleton used by the
           per-channel "Loading live numbers from Meta…" state. */
        @keyframes ci-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes ci-shimmer {
          0%   { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        .ci-root .ci-skel {
          display: inline-block;
          height: 20px;
          border-radius: 999px;
          background: linear-gradient(
            90deg,
            rgba(0,0,0,0.06) 25%,
            rgba(0,0,0,0.12) 37%,
            rgba(0,0,0,0.06) 63%
          );
          background-size: 400px 100%;
          animation: ci-shimmer 1.3s linear infinite;
        }
        .ci-root .ci-skel-pill { width: 68px; }
        @media (max-width: 768px) {
          .ci-root .ci-grid { grid-template-columns: 1fr; }
          .ci-root .ci-tiles { grid-template-columns: 1fr; }
          .ci-root .ci-header h1 { font-size: 22px; }
          .ci-root .bo-metric-value { font-size: 28px; }
        }
      `}</style>
      <div className="bo-page ci-root" data-testid="channel-insights-page">
        {/* The tiles read from activeReport = report || stale. On a
            failed refresh we keep the last-good `stale` so Meta review
            never sees three "Not connected" blocks at once. */}
        {(() => { return null; })()}
        <div className="ci-header">
          <div style={{ minWidth: 0, maxWidth: "100%" }}>
            <h1 style={{ margin: 0 }} data-testid="channel-insights-title">
              Channel Insights
            </h1>
            <div style={{ color: "var(--bo-muted)", marginTop: 4 }}>
              Live Facebook Page and Instagram account numbers, side by side.
              Any locked tile is a Meta permission that needs App Review approval.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center",
                        flexWrap: "wrap" }}>
            <div style={{ color: "var(--bo-muted)", fontSize: 13 }}
                 data-testid="channel-insights-pulled-at">
              {loading && !activeReport
                ? "Loading live numbers from Meta…"
                : activeReport?.pulled_at
                  ? `Pulled ${timeAgo(activeReport.cached_at || activeReport.pulled_at)}`
                  : "Pulling…"}
              {activeReport?.cache_hit && !loading && (
                <span style={{ marginLeft: 6, opacity: 0.75 }}>
                  (cached)
                </span>
              )}
              {refreshFailedUsingStale && (
                <span style={{ marginLeft: 6, color: "var(--bo-warn, #a86b00)" }}
                      data-testid="channel-insights-stale-note">
                  · refresh failed, showing cached
                </span>
              )}
            </div>
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              onClick={() => load({ force: true })}
              disabled={loading}
              data-testid="channel-insights-refresh"
            >
              {loading ? "Refreshing…" : "Refresh from Meta"}
            </button>
          </div>
        </div>

        {error && (
          <div className="bo-banner bo-banner-warn"
               style={{ marginBottom: 12 }}
               data-testid="channel-insights-error">
            {error}{" "}
            <button type="button" className="bo-link"
                    onClick={() => load({ force: true })}
                    data-testid="channel-insights-error-retry">
              Retry
            </button>
          </div>
        )}
        {retryMsg && (
          <div className="bo-banner" style={{ marginBottom: 12 }}
               data-testid="channel-insights-retry-msg">
            {retryMsg}
          </div>
        )}

        <div className="ci-grid" data-testid="channel-insights-grid">
          <ChannelColumn
            title="Facebook Page"
            block={activeReport?.facebook}
            tiles={FB_TILES}
            shortDays={shortDays}
            longDays={longDays}
            testId="channel-insights-fb"
            loading={loading}
          />
          <ChannelColumn
            title="Instagram"
            block={activeReport?.instagram}
            tiles={IG_TILES}
            shortDays={shortDays}
            longDays={longDays}
            testId="channel-insights-ig"
            onRetryIg={retrying ? null : onRetryIg}
            loading={loading}
          />
          <ChannelColumn
            title="Pinterest"
            block={activeReport?.pinterest}
            tiles={PINTEREST_TILES}
            shortDays={shortDays}
            longDays={longDays}
            testId="channel-insights-pn"
            loading={loading}
          />
        </div>

        <div style={{ marginTop: 24 }}>
          <h2 style={{ margin: "0 0 8px" }} data-testid="channel-insights-posts-title">
            Recent Facebook posts
          </h2>
          <div style={{ color: "var(--bo-muted)", marginBottom: 12, fontSize: 13 }}>
            Reach + clicks + reactions per post. Meta deprecates
            metric names often; the backend drops any invalid name and
            retries the rest so a single bad metric can't blank this
            table.
          </div>
          <PostsPanel
            posts={report?.posts || []}
            testId="channel-insights-posts"
          />
        </div>

        <div style={{ marginTop: 24 }}>
          <h2 style={{ margin: "0 0 8px" }}
              data-testid="channel-insights-ig-media-title">
            Recent Instagram posts
          </h2>
          <div style={{ color: "var(--bo-muted)", marginBottom: 12, fontSize: 13 }}>
            Per-media reach, views, likes, comments and saves via{" "}
            <code>/{"{"}ig-media-id{"}"}/insights</code>. Account-level
            numbers are thin for a brand-new Business account; this
            table makes the walkthrough meaningful by showing real
            per-post numbers.
          </div>
          <IgMediaPanel
            media={report?.instagram?.recent_media || []}
            testId="channel-insights-ig-media"
          />
        </div>

        <div style={{ marginTop: 24, fontSize: 12, color: "var(--bo-muted)" }}
             data-testid="channel-insights-footer">
          Graph API {report?.graph_version || "—"} · pulled {fmtWhen(report?.pulled_at)}
        </div>
      </div>
    </Shell>
  );
}
